import { Affix, Alert, Button, Card, Checkbox, Col, Divider, Dropdown, Empty, Modal, Popconfirm, Row, Space } from 'antd';
import { AuditOutlined, CalendarOutlined, ClearOutlined, DeleteFilled, DownOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useContext, useState, useRef } from 'react';
import AC from '../AccessControl';
import ToolkitsSearch from '../navigation/ToolkitsSearch';
import SectionHeader from '../panels/SectionHeader';
import EditableText from '../parameters/EditableText';
import FileInput from '../ui/FileInput';
import downloadFile from '../../utils/downloadFile';
import TasksTree from './TasksTree';
import CTX from '../../lib/AssignmentsContext';
const Playlist = () => {
  const {
    data,
    actions
  } = useContext(CTX);
  const treeRef = useRef();
  const [checked, setChecked] = useState([]);
  const [scrollFix, setScrollFix] = useState(false);
  const UploadFile = props => <FileInput accept=".yml" icon={<UploadOutlined />} onChange={files => {
    const reader = new FileReader();
    reader.onload = e => actions.deserialize(e.target.result, 'Loading playlist from a file');
    reader.readAsText(files[0]);
  }} {...props} data-sentry-element="FileInput" data-sentry-component="UploadFile" data-sentry-source-file="Playlist.jsx" />;
  const showResetConfirm = () => Modal.confirm({
    title: 'Clear Playlist?',
    content: 'All activities and assignments in your playlist will be removed. This cannot be undone.',
    okText: 'Clear Playlist',
    okType: 'primary',
    cancelType: 'link',
    cancelText: 'Cancel',
    onOk: actions.reset
  });
  const playlistOptions = {
    items: [{
      key: 'upload',
      icon: <UploadOutlined />,
      label: <UploadFile type="text">Upload Playlist</UploadFile>
    }, {
      key: 'download',
      icon: <DownloadOutlined />,
      label: <a type="text" onClick={() => downloadFile(actions.serialize(), data.label.replace(/\s+/g, '') + '.yml', 'application/x-yaml')}>
        Download Playlist
      </a>
    }, {
      key: 'reset',
      icon: <ClearOutlined />,
      label: <a onClick={showResetConfirm}>Reset</a>
    }]
  };
  return <div data-sentry-component="Playlist" data-sentry-source-file="Playlist.jsx">
      <SectionHeader data-sentry-element="SectionHeader" data-sentry-source-file="Playlist.jsx" />
      <Card title={<EditableText value={data.label} onChange={title => data.label = title} />} extra={<Dropdown menu={playlistOptions}>
            <Button block type="link">
              Manage Playlist <DownOutlined />
            </Button>
          </Dropdown>} data-sentry-element="Card" data-sentry-source-file="Playlist.jsx">
        <Affix offsetTop={64} onChange={bool => setScrollFix(bool)} data-sentry-element="Affix" data-sentry-source-file="Playlist.jsx">
          <div className={`playlist-actions ${scrollFix ? `playlist-actions-fixed` : ''}`}>
            <Space size="large" data-sentry-element="Space" data-sentry-source-file="Playlist.jsx">
              <Checkbox checked={treeRef.current?.areAllSelected()} indeterminate={!treeRef.current?.areAllSelected() && treeRef.current?.areSomeSelected()} onChange={e => treeRef.current?.checkAll(e.target.checked)} disabled={data.assignments.length === 0} data-sentry-element="Checkbox" data-sentry-source-file="Playlist.jsx">
                {!treeRef.current?.areAllSelected() && treeRef.current?.areSomeSelected() ? `Deselect All` : `Select All`}
              </Checkbox>
              <Popconfirm title={`Are you sure you want to remove ${treeRef.current?.checkedCount() ?? 0} activities?`} onConfirm={treeRef.current?.removeSelected} okText="Yes" cancelText="No" data-sentry-element="Popconfirm" data-sentry-source-file="Playlist.jsx">
                <Button danger type="link" icon={<DeleteFilled />} disabled={!treeRef.current?.areSomeSelected()} data-sentry-element="Button" data-sentry-source-file="Playlist.jsx">
                  Remove Selected
                </Button>
              </Popconfirm>
              <Button icon={<CalendarOutlined />} type="primary" onClick={() => actions.addAssignments()} data-sentry-element="Button" data-sentry-source-file="Playlist.jsx">
                Add New Assignment
              </Button>
              <ToolkitsSearch style={{
              width: 300
            }} placeholder="Search toolkits" allowClear enterButton data-sentry-element="ToolkitsSearch" data-sentry-source-file="Playlist.jsx" />
              <Button type="ghost" icon={<DownloadOutlined />} onClick={() => downloadFile(actions.serialize(), data.label.replace(/\s+/g, '') + '.yml', 'application/x-yaml')} data-sentry-element="Button" data-sentry-source-file="Playlist.jsx">
                Quick Save
              </Button>
            </Space>
          </div>
        </Affix>
        <Row gutter={[16, 16]} data-sentry-element="Row" data-sentry-source-file="Playlist.jsx">
          <Col span={18} data-sentry-element="Col" data-sentry-source-file="Playlist.jsx">
            <Card className="playlist-container" data-sentry-element="Card" data-sentry-source-file="Playlist.jsx">
              {data.assignments.length > 0 ? <TasksTree editable ref={treeRef} selected={checked} setSelected={setChecked} /> : <Empty image="/assets/sherlock_alert.png" imageStyle={{
              height: 180
            }} description={<div>
                      <p>There are currently 0 assignments in your playlist. </p>
                      <div className="button-group">
                        <AC.ButtonLink href={{
                  pathname: '/toolkits'
                }} label="Add New Activities" type="primary" />
                        <UploadFile>Upload a Playlist</UploadFile>
                      </div>
                    </div>} />}
            </Card>
          </Col>
          <Col span={6} data-sentry-element="Col" data-sentry-source-file="Playlist.jsx">
            <Card className="playlist-summary" title="PLAYLIST SUMMARY" data-sentry-element="Card" data-sentry-source-file="Playlist.jsx">
              <div className="description">
                <span className="label">Assignment Groups</span>
                <span className="value">{data.assignments.length}</span>
              </div>
              <div className="description">
                <span className="label">Total number of Activities</span>
                <span className="value">{data.assignments.map(a => a.data.tasks.length).reduce((s, v) => s + v, 0)}</span>
              </div>
              {!(data.assignments.length > 0 && data.assignments.every(({
              data
            }) => Object.values(data).every(Boolean) && data.tasks.length > 0)) && data.assignments.length > 0 && <Alert message="Missing Due Dates" type="warning" showIcon />}
              <Divider type="horizontal" data-sentry-element="Divider" data-sentry-source-file="Playlist.jsx" />
              <Button block className="submit-btn" size="large" type="primary" icon={<AuditOutlined />} onClick={() => actions.setListPrepared(true)} disabled={!(data.assignments.length > 0 && data.assignments.every(({
              data
            }) => Object.values(data).every(Boolean) && data.tasks.length > 0))} data-sentry-element="Button" data-sentry-source-file="Playlist.jsx">
                Assign Playlist
              </Button>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>;
};
export default Playlist;